import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useHasFeature } from '@/lib/useHasFeature';
import { MY_CLIP_PUBLISHES } from '@/queries/publishing';
import { mapPublishStatus } from '@/lib/publishing/mapPublishStatus';
import { resolvePublishAction } from '@/lib/publishing/resolvePublishAction';
import {
  isPublishPlatform,
  type PublishDialogTarget,
  type PublishMediaKinds,
  type PublishPlatform,
  type PublishRecord,
  type PublishUiStatus,
} from '@/lib/publishing/types';

interface ClipPublishSummary {
  id: string;
  platform: PublishPlatform;
  status: string;
  fail_reason: string | null;
  media_kind: string | null;
  createdAt: string | null;
}

interface PublishDiscoveryTarget {
  key: string;
  clipId: string;
  previousRowId: string | null;
  expiresAt: number;
}

const PUBLISH_DISCOVERY_TIMEOUT_MS = 30_000;

/** Permissive structural type — each clip page passes its own generated
 * `myClips` element type, which only needs these shared display fields. */
export interface ClipPublishingClip {
  id: string;
  name?: string | null;
}

export interface UseClipPublishingResult {
  hasProFeatures: boolean;
  /** Most recent actionable publish attempt per `clipId:platform`. */
  publishByClip: Map<string, ClipPublishSummary>;
  getPublishStatus: (clipId: string, platform?: PublishPlatform) => PublishUiStatus;
  getPublishFailReason: (clipId: string, platform?: PublishPlatform) => string | null;
  /** `media_kind` of the latest publish on each platform (carousel icon). */
  getPublishMediaKinds: (clipId: string) => PublishMediaKinds;
  showPublishProGate: boolean;
  setShowPublishProGate: (open: boolean) => void;
  publishTarget: PublishDialogTarget | null;
  setPublishTarget: (target: PublishDialogTarget | null) => void;
  handlePublish: (clipId: string, platform: PublishPlatform) => void;
  resolvePublishHandler: (
    hasFinishedVideo: boolean,
    platform: PublishPlatform,
  ) => ((clipId: string) => void) | undefined;
  /** Looks up a clip's display name for any platform dialog. */
  resolveClipTitle: (clipId: string) => string;
  /** Explicitly refetches because the Apollo client uses `no-cache`. */
  refetchPublishes: (acceptedTarget?: PublishDialogTarget) => void;
}

function publishKey(clipId: string, platform: PublishPlatform): string {
  return `${clipId}:${platform}`;
}

function timestampOf(value: string | null): number | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

/** Different valid timestamps select the newer row. Ties, or a missing/invalid
 * timestamp on either row, preserve the backend's stable descending order. */
function isNewerPublish(candidate: ClipPublishSummary, current: ClipPublishSummary): boolean {
  const candidateTimestamp = timestampOf(candidate.createdAt);
  const currentTimestamp = timestampOf(current.createdAt);
  if (candidateTimestamp === null || currentTimestamp === null) return false;
  return candidateTimestamp > currentTimestamp;
}

/** Shared multi-platform publishing state for a page's visible clip grid. */
export function useClipPublishing(clips: ClipPublishingClip[]): UseClipPublishingResult {
  const hasProFeatures = useHasFeature('pro_features');
  const [publishTarget, setPublishTarget] = useState<PublishDialogTarget | null>(null);
  const [showPublishProGate, setShowPublishProGate] = useState(false);
  const [discoveryTargets, setDiscoveryTargets] = useState<Map<string, PublishDiscoveryTarget>>(
    () => new Map(),
  );
  const [pollInterval, setPollInterval] = useState(0);

  const clipIds = useMemo(() => [...new Set(clips.map((clip) => clip.id))], [clips]);
  const visibleClipIds = useMemo(() => new Set(clipIds), [clipIds]);

  const publishQuery = useQuery(MY_CLIP_PUBLISHES, {
    variables: { clipIds },
    skip: clipIds.length === 0,
    pollInterval: clipIds.length === 0 ? 0 : pollInterval,
  });
  const { data: publishData, refetch } = publishQuery;

  const publishRows = useMemo(
    () => (publishData?.myClipPublishes || []) as PublishRecord[],
    [publishData],
  );

  const publishByClip = useMemo(() => {
    const map = new Map<string, ClipPublishSummary>();
    for (const row of publishRows) {
      if (!row.clip_id || !visibleClipIds.has(row.clip_id) || !isPublishPlatform(row.platform)) continue;
      const key = publishKey(row.clip_id, row.platform);
      const candidate: ClipPublishSummary = {
        id: row.id,
        platform: row.platform,
        status: row.status,
        fail_reason: row.fail_reason ?? null,
        media_kind: row.media_kind ?? null,
        createdAt: row.createdAt ?? null,
      };
      const current = map.get(key);
      if (!current || isNewerPublish(candidate, current)) map.set(key, candidate);
    }
    return map;
  }, [publishRows, visibleClipIds]);

  const hasActivePublish = [...publishByClip.values()].some(
    (row) => row.status === 'pending' || row.status === 'processing',
  );

  useEffect(() => {
    setPollInterval(
      clipIds.length > 0 && (hasActivePublish || discoveryTargets.size > 0) ? 5000 : 0,
    );
  }, [clipIds.length, discoveryTargets.size, hasActivePublish]);

  const refetchPublishes = useCallback((acceptedTarget?: PublishDialogTarget) => {
    if (clipIds.length === 0) return;
    const target = acceptedTarget ?? publishTarget;
    if (target && visibleClipIds.has(target.clipId)) {
      const key = publishKey(target.clipId, target.platform);
      setDiscoveryTargets((current) => {
        const next = new Map(current);
        next.set(key, {
          key,
          clipId: target.clipId,
          previousRowId: publishByClip.get(key)?.id ?? null,
          expiresAt: Date.now() + PUBLISH_DISCOVERY_TIMEOUT_MS,
        });
        return next;
      });
    }
    try {
      void Promise.resolve(refetch()).catch(() => undefined);
    } catch {
      // A synchronous test/client failure is treated like a rejected refetch;
      // bounded discovery polling remains responsible for finding the row.
    }
  }, [clipIds.length, publishByClip, publishTarget, refetch, visibleClipIds]);

  useEffect(() => {
    if (discoveryTargets.size === 0) return;
    setDiscoveryTargets((currentTargets) => {
      let changed = false;
      const next = new Map(currentTargets);
      for (const target of currentTargets.values()) {
        const current = publishByClip.get(target.key);
        if (
          current
          && current.id !== target.previousRowId
          && mapPublishStatus(current.status, current.platform) !== 'none'
        ) {
          next.delete(target.key);
          changed = true;
        }
      }
      return changed ? next : currentTargets;
    });
  }, [discoveryTargets.size, publishByClip]);

  useEffect(() => {
    if (discoveryTargets.size === 0) return undefined;
    const earliestExpiry = Math.min(
      ...[...discoveryTargets.values()].map((target) => target.expiresAt),
    );
    const timeout = window.setTimeout(() => {
      const now = Date.now();
      setDiscoveryTargets((currentTargets) => {
        const next = new Map(currentTargets);
        for (const target of currentTargets.values()) {
          if (target.expiresAt <= now) next.delete(target.key);
        }
        return next.size === currentTargets.size ? currentTargets : next;
      });
    }, Math.max(0, earliestExpiry - Date.now()));
    return () => window.clearTimeout(timeout);
  }, [discoveryTargets]);

  useEffect(() => {
    if (publishTarget && !visibleClipIds.has(publishTarget.clipId)) setPublishTarget(null);
    setDiscoveryTargets((currentTargets) => {
      const next = new Map(currentTargets);
      for (const target of currentTargets.values()) {
        if (!visibleClipIds.has(target.clipId)) next.delete(target.key);
      }
      return next.size === currentTargets.size ? currentTargets : next;
    });
  }, [publishTarget, visibleClipIds]);

  const getPublishStatus = useCallback(
    (clipId: string, platform: PublishPlatform = 'tiktok'): PublishUiStatus => (
      mapPublishStatus(publishByClip.get(publishKey(clipId, platform))?.status, platform)
    ),
    [publishByClip],
  );

  const getPublishFailReason = useCallback(
    (clipId: string, platform: PublishPlatform = 'tiktok'): string | null => (
      publishByClip.get(publishKey(clipId, platform))?.fail_reason ?? null
    ),
    [publishByClip],
  );

  const getPublishMediaKinds = useCallback(
    (clipId: string): PublishMediaKinds => ({
      tiktok: publishByClip.get(publishKey(clipId, 'tiktok'))?.media_kind ?? null,
      instagram: publishByClip.get(publishKey(clipId, 'instagram'))?.media_kind ?? null,
      youtube: publishByClip.get(publishKey(clipId, 'youtube'))?.media_kind ?? null,
      facebook: publishByClip.get(publishKey(clipId, 'facebook'))?.media_kind ?? null,
    }),
    [publishByClip],
  );

  const resolveClipTitle = useCallback(
    (clipId: string): string => clips.find((clip) => clip.id === clipId)?.name || '',
    [clips],
  );

  const handlePublish = useCallback((clipId: string, platform: PublishPlatform) => {
    const action = resolvePublishAction({ isPro: hasProFeatures, eligibility: 'eligible' });
    if (action === 'pro_gate') {
      setPublishTarget(null);
      setShowPublishProGate(true);
      return;
    }
    setShowPublishProGate(false);
    setPublishTarget({ clipId, platform });
  }, [hasProFeatures]);

  const resolvePublishHandler = useCallback((
    hasFinishedVideo: boolean,
    platform: PublishPlatform,
  ) => {
    const action = resolvePublishAction({
      isPro: hasProFeatures,
      eligibility: hasFinishedVideo ? 'eligible' : 'not_rendered',
    });
    if (action === 'hidden') return undefined;
    return (clipId: string) => handlePublish(clipId, platform);
  }, [hasProFeatures, handlePublish]);

  return {
    hasProFeatures,
    publishByClip,
    getPublishStatus,
    getPublishFailReason,
    getPublishMediaKinds,
    showPublishProGate,
    setShowPublishProGate,
    publishTarget,
    setPublishTarget,
    handlePublish,
    resolvePublishHandler,
    resolveClipTitle,
    refetchPublishes,
  };
}
